import { useMutation } from '@apollo/client/react';
import { UploadResult } from '@uppy/core';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import { Dashboard } from '@uppy/react';
import { MediaType, PutMediaDocument } from '@website/types/graphqlOperations';
import { Dispatch, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { ReducerType } from './MediaManager';
import { RequireUserIdOrHostId, createFilename, useUppy } from './useUppy';
import { updateMediaCache } from './utils/update_media_cache';

/**
 * Determin the media type (e.g. video or image) from the file type
 */
export function determineMediaType(fileType: string): MediaType {
  if (fileType.includes('video')) {
    return MediaType.VIDEO;
  }
  return MediaType.IMAGE;
}

/**
 * Upload Media Props
 *
 * Props must have either a userID or hostID
 */
type Props = RequireUserIdOrHostId & {
  tripID?: string;
  groupID?: string;
  selectImageDispatcher?: Dispatch<ReducerType>;
  allowMultipleSelect?: boolean;
};

/**
 * Media Uploader (Inline)
 *
 * Uploads media to AWS S3 (via Transloadit), and then submits a mutation to
 * update the media database.
 */
export default function UploadMediaToTransLoadIt({
  tripID,
  groupID,
  hostID,
  userID,
  selectImageDispatcher = () => {},
  allowMultipleSelect = false,
}: Props) {
  const [uppy, isLoadingUppy] = useUppy({
    allowMultipleSelect,
    userID,
    hostID,
  });

  const [putMedia] = useMutation(PutMediaDocument, {
    update(cache, { data }) {
      updateMediaCache({
        cache,
        data,
        tripID,
        groupID,
        hostID,
        userID,
      });
    },
  });

  // When the file has been uploaded, we need to add it to the database with a mutation
  useEffect(() => {
    const onCompleteHandler = async (
      result: UploadResult<Record<string, unknown>, Record<string, unknown>>,
    ) => {
      const { data } = await putMedia({
        variables: {
          input: {
            files: result.successful.map((file) => ({
              // The image was put in either the hosts or userID directory in
              // S3, default to root if either isn't present
              key: createFilename({ hostID, userID, fileName: file.name }),
              type: determineMediaType(file.type),
            })),
            hostID,
            tripID,
            userID,
            groupID,
          },
        },
      });

      // Add images so they can be used
      data.putMedia.forEach((media) => {
        selectImageDispatcher({
          type: 'add',
          value: media,
          allowMultipleSelect,
        });
      });
    };

    // Once files have been uploaded, this  is triggered and you can put it to DynamoDB
    uppy?.on('complete', onCompleteHandler);

    return () => {
      uppy?.off('complete', onCompleteHandler);
    };
  }, [
    hostID,
    uppy,
    putMedia,
    selectImageDispatcher,
    tripID,
    userID,
    allowMultipleSelect,
    groupID,
  ]);

  if (isLoadingUppy) {
    return <Spinner animation="border" />;
  }

  return (
    <>
      <style jsx global>{`
        .uppy-Root,
        .uppy-Dashboard,
        .uppy-Dashboard-inner {
          height: 100%;
        }
      `}</style>

      <div
        style={{
          height: '29rem',
          maxHeight: '80vh',
        }}
      >
        <div className="d-block" style={{ height: '100%' }}>
          <Dashboard
            width="100%"
            height="100%"
            style={{ height: '100%' }}
            uppy={uppy}
            showProgressDetails
            // Hide the done button as we upload the photos before it's clicked
            doneButtonHandler={null}
            plugins={[
              'unsplash',
              'google-drive',
              'facebook',
              'dropbox',
              'onedrive',
              'instagram',
            ]}
            // Uppy creates blobs (in memory) of thumbnails after uploading. These are displayed at anything up to 400px
            // wide so setting the width here simply makes sure it's not being upscaled (it does not set the max display
            // size of the image).
            thumbnailWidth={400}
            proudlyDisplayPoweredByUppy={false}
          />
        </div>
      </div>
    </>
  );
}
